// ─── Analytics ──────────────────────────────────────────────────────────────
// posthog-js, firing the FULL event set from docs/retention-gate-protocol.md §6.
// The one the whole retention gate is built on — `app_opened` — fires on load
// and on every return-to-foreground. Names match the protocol doc exactly.
//
// Events N/A on web v0 (no sign-in, no push): signin_prompted, signin_completed,
// notification_sent, notification_opened, ghost_replay_*. Everything else fires.

import posthog from 'posthog-js';
import { POSTHOG_KEY, POSTHOG_HOST } from './config';

type Props = Record<string, string | number | boolean | null>;

type Event =
  | { event: 'app_opened'; props: { session_number: number; source: 'load' | 'foreground' } }
  | { event: 'game_started'; props: { mode: 'daily' | 'onboarding' } }
  | { event: 'game_over'; props: { score: number; chains_cleared: number; max_combo: number; on_beat_pct: number; total_moves: number; is_pb: boolean; mode: 'daily' | 'onboarding' } }
  | { event: 'chain_cleared'; props: { color: string; combo: number; length: number; was_on_beat: boolean } }
  | { event: 'token_placed'; props: { lane: number } }
  | { event: 'max_combo_reached'; props: { max_combo: number } }
  | { event: 'beat_sync_bonus'; props: { combo: number } }
  | { event: 'game_over_screen_viewed'; props: Props }
  | { event: 'share_card_tapped'; props: { method: 'native' | 'clipboard' } }
  | { event: 'share_completed'; props: { method: 'native' | 'clipboard' } }
  | { event: 'leaderboard_viewed'; props: Props }
  | { event: 'onboarding_chain_fired'; props: { moves: number } }
  | { event: 'streak_milestone_reached'; props: { streak: number } }
  | { event: 'iap_initiated'; props: { entitlement: string } }
  | { event: 'iap_completed'; props: { entitlement: string } }
  | { event: 'skin_changed'; props: { skin: string } };

let ready = false;

export function initAnalytics(): void {
  if (!POSTHOG_KEY) {
    console.warn('[SortBeat] PostHog key not set — analytics disabled.');
    return;
  }
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false, // explicit events only
    capture_pageview: false, // app_opened is our pageview
    persistence: 'localStorage', // cookieless — lighter GDPR footprint
    person_profiles: 'identified_only',
  });
  ready = true;
}

export function track(e: Event): void {
  if (!ready) return;
  posthog.capture(e.event, e.props);
}

/** session_number for the app_opened cohort — 1 on first ever visit, then +1. */
export function bumpSession(): number {
  let n = 0;
  try {
    n = Number(localStorage.getItem('sb_session') ?? '0') + 1;
    localStorage.setItem('sb_session', String(n));
  } catch {
    n = 1;
  }
  return n;
}
